// The escape-time maths, and nothing else.
//
// Lifted out of renderCore.js when the fixed colour map landed (FINAL-PHASE-PLAN
// F2). The reason is a dependency cycle, not tidiness: colourMap.js has to sample
// a region's escape values to derive the mapping, and renderCore.js has to import
// that mapping to colour a render. One of the two had to stop importing the other,
// and this is the piece both genuinely share.
//
// The resulting graph is acyclic and one-directional:
//
//   escape.js  <--  colourMap.js  <--  renderCore.js
//        \_________________________________/
//
// renderCore.js re-exports everything below, so every existing importer — the
// tests, the scripts in scripts/ — is unaffected and there is still one obvious
// place to `import { escapeVelocity }` from.
//
// Note the explicit .js extensions this file has none of and needs none of: it
// imports nothing. Anything added here must follow renderCore.js's rule, because
// this module is on the headless path (print renders, and Block J's og:image).

export const MAX_ITERATIONS = 1000;

// Bailout radius 2^8. A high radius (vs. the classic 2) leaves the escaped |z|
// well past the boundary, which makes the continuous-count smoothing far cleaner
// — the fractional overshoot we subtract is more accurate. BAILOUT_SQ lets the
// escape loop compare squared magnitudes and skip the per-iteration sqrt.
export const BAILOUT = 256;
const BAILOUT_SQ = BAILOUT * BAILOUT;
const LOG2 = Math.log(2);

// The continuous (smoothed) escape count for c = real + imaginary·i. Returns NaN
// for interior points (never escaped within MAX_ITERATIONS).
//
// The integer iteration count is what produced the old banding — whole numbers
// step the colour in visible rings. Smoothing recovers a fractional count by
// measuring how far past the bailout radius |z| overshot on the final step:
//   n + 1 − log2( log|z| )
// which slides continuously between integer counts and kills the bands. The
// escape loop is the standard z ← z² + c, unrolled onto scalars (re, im and their
// squares) so it can test the squared magnitude and avoid a per-iteration sqrt.
export function escapeVelocity(real, imaginary) {

    let re = 0;
    let im = 0;
    let re2 = 0;
    let im2 = 0;
    let n = 0;

    while (re2 + im2 <= BAILOUT_SQ && n < MAX_ITERATIONS) {
        im = 2 * re * im + imaginary;
        re = re2 - im2 + real;
        re2 = re * re;
        im2 = im * im;
        n++;
    }

    if (n >= MAX_ITERATIONS) return NaN;  // interior

    const magnitude = Math.sqrt(re2 + im2);
    return n + 1 - Math.log(Math.log(magnitude)) / LOG2;
}
